import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  FaPlus, FaArrowUp, FaArrowDown, FaBan, FaLayerGroup, FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../Modal";
import PreguntasAdmin from "./PreguntasAdmin";

export default function SeccionesAdmin({ encuestaId }) {

  const { token } = useContext(AuthContext);

  const [secciones, setSecciones] = useState([]);
  const [nombre, setNombre] = useState("");
  const [creando, setCreando] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [seccionEstado, setSeccionEstado] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const fetchSecciones = async () => {
    if (!token) return;
    const res = await fetch(`/api/admin/secciones/${encuestaId}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });
    setSecciones(await res.json());
  };

  useEffect(() => {
    fetchSecciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const crear = async () => {
    if (!nombre.trim()) {
      mostrarToast("Escribe un nombre para la sección", "error");
      return;
    }

    setCreando(true);
    try {
      const res = await fetch("/api/admin/secciones", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ encuesta_id: encuestaId, nombre, orden: secciones.length + 1 }),
      });

      if (!res.ok) {
        mostrarToast("Error creando la sección", "error");
        return;
      }

      setNombre("");
      mostrarToast("Sección creada");
      fetchSecciones();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    } finally {
      setCreando(false);
    }
  };

  const actualizar = async (sec) => {
    try {
      const res = await fetch(`/api/admin/secciones/${sec.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sec),
      });
      if (!res.ok) mostrarToast("Error guardando cambios", "error");
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  const abrirConfirmEstado = (sec) => {
    setSeccionEstado(sec);
    setConfirmOpen(true);
  };

  const confirmarCambioEstado = async () => {
    const activar = !seccionEstado.activo;
    await actualizar({ ...seccionEstado, activo: activar });
    mostrarToast(activar ? "Sección activada" : "Sección desactivada");
    setConfirmOpen(false);
    fetchSecciones();
  };

  const mover = async (index, dir) => {
    const nueva = [...secciones];
    const temp = nueva[index];
    nueva[index] = nueva[index + dir];
    nueva[index + dir] = temp;

    setSecciones(nueva);

    for (let i = 0; i < nueva.length; i++) {
      await actualizar({ ...nueva[i], orden: i + 1 });
    }
    fetchSecciones();
  };

  return (
    <div>
      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      <h3 className="text-sm font-semibold text-slate-700 mb-3">Secciones</h3>

      {/* CREAR */}
      <div className="flex gap-2 mb-4">
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nueva sección"
          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
        />
        <button
          onClick={crear}
          disabled={creando}
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Agregar
        </button>
      </div>

      {/* LISTA */}
      {secciones.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">Aún no hay secciones en esta encuesta.</p>
      ) : (
        <div className="space-y-3">
          {secciones.map((sec, index) => (
            <div key={sec.id} className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <FaLayerGroup className="text-xs" />
                </span>

                <input
                  value={sec.nombre}
                  onChange={(e) => {
                    const nueva = [...secciones];
                    nueva[index].nombre = e.target.value;
                    setSecciones(nueva);
                  }}
                  onBlur={() => actualizar(sec)}
                  className="flex-1 min-w-[140px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    disabled={index === 0}
                    onClick={() => mover(index, -1)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition"
                    title="Subir"
                  >
                    <FaArrowUp className="text-[10px]" />
                  </button>
                  <button
                    disabled={index === secciones.length - 1}
                    onClick={() => mover(index, 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition"
                    title="Bajar"
                  >
                    <FaArrowDown className="text-[10px]" />
                  </button>
                  <button
                    onClick={() => abrirConfirmEstado(sec)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-rose-200 text-rose-500 hover:bg-rose-50 transition"
                    title="Desactivar"
                  >
                    <FaBan className="text-[10px]" />
                  </button>
                </div>
              </div>

              <div className="mt-3 pl-9">
                <PreguntasAdmin seccionId={sec.id} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL CONFIRMAR ACTIVAR/DESACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            seccionEstado?.activo ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-500"
          }`}>
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {seccionEstado?.activo ? "Desactivar" : "Activar"} sección
          </h3>
          <p className="text-sm text-slate-500 mb-6">¿Deseas continuar?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarCambioEstado}
              className={`w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
                seccionEstado?.activo ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
